<template lang="pug">
v-container.pa-8(fluid style="max-width:1600px")
  v-row.mb-2(no-gutters align="center")
    v-col.shrink
      v-btn.ml-n3(text icon @click="onClickBack" height)
        v-icon(color="neutral700" :size="24") mdi-arrow-left
    v-col.ml-3
      .text-2xl.font-semibold.neutral700--text Reservations & pre-orders
  v-row.pb-2.pb-md-0(no-gutters)
    v-col
      v-tabs(v-model="currentTabIndex" color="primary" :height="44" active-class="font-semibold" @change="tabChanged")
        v-tab.text-sm.font-medium.text-none.custom-tab-width
          | Reservation
          span.ml-1(v-if="activeReservationCount") ({{ activeReservationCount }})
        v-tab.text-sm.font-medium.text-none.custom-tab-width
          | Pre-order
          span.ml-1(v-if="activePreorderCount") ({{ activePreorderCount }})
    v-col.shrink(align="end")
      div(style="width: 340px;")
        .d-flex.align-center
          v-spacer
          v-btn(@click="clickSearch" icon color="neutral500")
            v-icon(size="20") $yzi-search-sm
          v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
          template(v-if="!showSearchField")
            div
              customize-column-menu(:type="isTabReservation ? 'reservation' : 'preorder'" :showTooltip="false" iconSize="21")
            v-menu(offset-y bottom right rounded="lg" attach)
              template(v-slot:activator="{ on }")
                v-btn.button-sm.ml-4(v-on="on" color="neutral300" depressed)
                  span.yz-medium-body-sm Add new
                  v-icon(right size="20") mdi-chevron-down
              v-list.py-0(style="border-radius: 8px; text-align: left;")
                v-list-item.clickable.hover-bg(@click="onAddNewReservation")
                  v-list-item-title.text-sm.basic800--text Add new reservation
                v-divider
                v-list-item.clickable.hover-bg(@click="onAddNewPreorder")
                  v-list-item-title.text-sm.basic800--text Add new pre-order
  v-divider
  div.d-flex.justify-center.mt-16(v-if="isChangingTab")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
  div.mb-4(v-else)
    div(v-if="isTabReservation")
      reservation-filter-row.my-2(ref="reservationFilter" :filters.sync="reservationFilters" :prop-filters="saveReservationFilters")
    div(v-else-if="isTabPreorder")
      preorder-filter-row.my-2(ref="preorderFilter" :filters.sync="preorderFilters" :prop-filters="savePreorderFilters")
    .data-table
      v-data-table.table-custom.pb-6(:headers="headers" :items="items" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2" :server-items-length="totalCount" @update:sort-by="sortBy = $event" @update:sort-desc="sortIsDesc = $event" @click:row="onSelectRow")
        template(#loading)
          v-skeleton-loader(type="table-tbody")
        template(#no-data)
          .mt-6
            v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
            .mt-2.p2.basic600--text {{ $t('no_list_available') }}

        //- patient
        template(v-slot:item.patient="{ item, index }")
          div(:id="`pt-${index}`" style="position: relative;")
          div.d-flex.my-3(v-if="item.patient")
            div(style="width: 30px;")
              v-responsive(:aspect-ratio="7/10" style="width: 30px;")
                v-card.fill-height(v-if="item.patient.profile_image && item.patient.profile_image.file" flat)
                  v-img(:src="`https://img.yezza.io/50x50/${item.patient.profile_image.path}`" height="100%" width="100%")
                    template(v-slot:placeholder)
                      v-row.fill-height.ma-0(align="center" justify="center")
                        v-progress-circular(indeterminate color="grey")
                v-card.neutral300.fill-height(v-else flat)
                  .d-flex.align-center.justify-center.fill-height
                    v-icon(size="20") $yzi-user-03
            div.pl-2
              v-tooltip(top color="black" :attach="`#pt-${index}`" content-class="black tooltip-top-arrow")
                template(#activator="{ on, attrs }")
                  div(v-bind="attrs" v-on="on")
                    .text-sm.font-semibold.neutral700--text.ellipsis-text-1 {{ item.patient.name }}
                div
                  .text-sm.font-semibold {{ item.patient.name }}
                  .text-xs(v-if="item.patient.dob") {{ $options.filters.getAgeByYearMonthDay(item.patient.dob) | displayAgeByYearMonthDay }} old
              div.d-flex.align-start.flex-column
                .text-xs.neutral700--text.no-wrap(v-if="item.patient.dob") {{ $options.filters.getAgeByYearMonthDay(item.patient.dob) | displayAgeByYearMonthDay }} old

        //- reservation no
        template(v-slot:item.reservation_no="{ item }")
          .text-sm.font-semibold.neutral700--text {{ item.rid }}
        //- pre-order no
        template(v-slot:item.preorder_no="{ item }")
          .text-sm.font-semibold.neutral700--text {{ item.rid }}
        //- item (products)
        template(v-slot:item.item="{ item }")
          yz-truncated-list.my-3(:items="getItemNames(item)")
        //- payment status
        template(v-slot:item.payment_status="{ item }")
          .d-flex.align-center
            v-icon.mr-2(:color="getPaymentStatusColor(item)" size="22") {{ getPaymentStatusIcon(item) }}
            .text-sm {{ getPaymentStatusLabel(item) }}
        //- reservation status
        template(v-slot:item.reservation_status="{ item }")
          y-chip(:label="getReservationStatusLabel(item.status)" :color="getReservationStatusChipColor(item.status)")
        //- preorder status
        template(v-slot:item.preorder_status="{ item }")
          y-chip(:label="getReservationStatusLabel(item.status)" :color="getReservationStatusChipColor(item.status)")

        //- actions
        template(v-slot:item.actions="{ item, index }")
          //- Reservation actions
          div(v-if="isTabReservation" @click.stop)
            v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg" attach)
              template(#activator="{ on }")
                v-btn(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0(style="border-radius: 8px;")
                yz-tooltip(v-if="item.status !== 'cancelled'" :text="item.status === 'fulfilled' ? 'This reservation has been fulfilled' : 'No products in this reservation'" :disabled="item.status !== 'fulfilled' && getItemNames(item).length > 0" direction="left")
                  v-list-item(@click="item.status !== 'fulfilled' && getItemNames(item).length > 0 && onRegisterAndAddToVisit(item)" :disabled="item.status === 'fulfilled' || getItemNames(item).length === 0")
                    v-list-item-title.text-sm.basic800--text Register & add to visit
                v-divider(v-if="item.status !== 'cancelled'")
                v-list-item(@click="onViewReservation(item)")
                  v-list-item-title.text-sm.basic800--text View reservation
                v-divider(v-if="item.status !== 'cancelled'")
                yz-tooltip(v-if="item.status !== 'cancelled'" text="This reservation has been fulfilled" :disabled="item.status !== 'fulfilled'" direction="left")
                  v-list-item(:disabled="item.status === 'fulfilled'" @click="item.status !== 'fulfilled' && onCancelReservation(item)")
                    v-list-item-title.text-sm.danger500--text Cancel reservation

          //- Pre-order actions
          div(v-else-if="isTabPreorder" @click.stop)
            v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg" attach)
              template(#activator="{ on }")
                v-btn(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0(style="border-radius: 8px;")
                yz-tooltip(v-if="item.status !== 'cancelled'" :text="item.status === 'fulfilled' ? 'This pre-order has been fulfilled' : 'No products in this pre-order'" :disabled="item.status !== 'fulfilled' && getItemNames(item).length > 0" direction="left" no-arrow)
                  v-list-item(@click="item.status !== 'fulfilled' && getItemNames(item).length > 0 && onRegisterAndAddToVisit(item)" :disabled="item.status === 'fulfilled' || getItemNames(item).length === 0")
                    v-list-item-title.text-sm.basic800--text Register & add to visit
                v-divider(v-if="item.status !== 'cancelled'")
                v-list-item(@click="onViewPreorder(item)")
                  v-list-item-title.text-sm.basic800--text View pre-order
                v-divider(v-if="item.status !== 'cancelled'")
                yz-tooltip(v-if="item.status !== 'cancelled'" text="This pre-order has been fulfilled" :disabled="item.status !== 'fulfilled'" direction="left" no-arrow)
                  v-list-item(:disabled="item.status === 'fulfilled'" @click="item.status !== 'fulfilled' && onCancelPreorder(item)")
                    v-list-item-title.text-sm.danger500--text Cancel pre-order

    div.d-flex.align-center(v-if="totalCount > pageSize")
      v-spacer
      yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  .dialogs
    reservation-preorder-patient-dialog(v-if="showReservationPreorderPatientDialog" :open.sync="showReservationPreorderPatientDialog" :type="reservationDialogType")
    cancel-reservation-preorder-dialog(v-if="showCancelReservationDialog" v-model="showCancelReservationDialog" :type="cancelReservationType" :reservation-data="selectedReservationItem" :loading="isCancellingReservation" @confirm="onConfirmCancelReservation" @refund="onProceedWithRefund")
    confirm-register-and-add-to-visit-dialog(v-if="showRegisterAddToVisitDialog" v-model="showRegisterAddToVisitDialog" :type="isTabReservation ? 'reservation' : 'preorder'" :reservation-data="pendingRegisterItem" @confirm="onConfirmRegisterAndAddToVisit")
    register-patient-dialog(v-if="openRegisterPatientDialog" :open.sync="openRegisterPatientDialog" :existing-patient.sync="tempPatient" :reservation-fulfill-item="reservationFulfillItem" @closed="closeDialog")
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import ReservationFilterRow from '@/components/clinic/registration/ReservationFilterRow.vue'
import PreorderFilterRow from '@/components/clinic/registration/PreorderFilterRow.vue'
import ReservationPreorderPatientDialog from '@/components/clinic/registration/ReservationPreorderPatientDialog.vue'
import CancelReservationPreorderDialog from '@/components/clinic/reservation-preorder/CancelReservationPreorderDialog.vue'
import ConfirmRegisterAndAddToVisitDialog from '@/components/clinic/registration/ConfirmRegisterAndAddToVisitDialog.vue'
import RegisterPatientDialog from '@/components/clinic/registration/RegisterPatientDialog.vue'

export default {
  name: 'ReservationsPreorders',
  props: ['savePage', 'saveTab', 'saveSearch', 'saveReservationFilters', 'savePreorderFilters'],
  components: {
    CustomizeColumnMenu,
    ReservationFilterRow,
    PreorderFilterRow,
    ReservationPreorderPatientDialog,
    CancelReservationPreorderDialog,
    ConfirmRegisterAndAddToVisitDialog,
    RegisterPatientDialog,
  },
  data () {
    return {
      currentTabIndex: 0,
      isChangingTab: false,
      isFetching: false,
      showSearchField: false,
      searchText: null,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      items: [],
      reservationFilters: [],
      preorderFilters: [],
      activeReservationCount: 0,
      activePreorderCount: 0,
      showReservationPreorderPatientDialog: false,
      reservationDialogType: 'reservation',
      showCancelReservationDialog: false,
      isCancellingReservation: false,
      cancelReservationType: 'reservation',
      selectedReservationItem: null,
      showRegisterAddToVisitDialog: false,
      pendingRegisterItem: null,
      openRegisterPatientDialog: false,
      tempPatient: null,
      sortBy: null,
      sortIsDesc: false,
      ordering: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      reservationFields: 'clinic/getReservationFields',
      preorderFields: 'clinic/getPreorderFields',
    }),
    isTabReservation () {
      return this.currentTabIndex === 0
    },
    isTabPreorder () {
      return this.currentTabIndex === 1
    },
    reservationFulfillItem () {
      if (!this.pendingRegisterItem) return null
      if (this.isTabReservation) return { id: this.pendingRegisterItem.id, type: 'reservation' }
      if (this.isTabPreorder) return { id: this.pendingRegisterItem.id, type: 'preorder' }
      return null
    },
    headers () {
      const patient = { text: 'Patient', value: 'patient', enable: true, sort_query_param: 'lead__name' }
      const actions = { text: '', value: 'actions', enable: true, sortable: false }
      const source = this.isTabReservation ? this.reservationFields : this.preorderFields
      const fields = source.filter(f => f.enable).map(f => ({
        ...f,
        text: f.field_name,
        value: f.id,
      }))
      return [patient, ...fields, actions]
    },
  },
  watch: {
    reservationFilters () {
      if (!this.isChangingTab) this.fetchList()
    },
    preorderFilters () {
      if (!this.isChangingTab) this.fetchList()
    },
    ordering () {
      this.currentPage = 1
      this.fetchList()
    },
    isChangingTab () {
      this.ordering = null
      this.sortBy = null
      this.sortIsDesc = false
    },
  },
  mounted () {
    this.checkRoute()
    const activeRef = this.isTabReservation ? 'reservationFilter' : 'preorderFilter'
    const activeSaved = this.isTabReservation ? this.saveReservationFilters : this.savePreorderFilters
    if (activeSaved) {
      this.$nextTick(() => {
        if (this.$refs[activeRef]) this.$refs[activeRef].setFilters()
      })
    } else {
      this.fetchList()
    }
    this.fetchActiveCounts()
  },
  methods: {
    ...mapActions(['showMessage']),
    onClickBack () {
      this.$router.push({ name: 'ClinicRegistration' })
    },
    checkRoute () {
      if (this.saveSearch) {
        this.searchText = this.saveSearch
        this.clickSearch()
      }
      if (this.savePage) {
        this.currentPage = this.savePage
      }
      if (this.saveTab) {
        this.currentTabIndex = this.saveTab
      }
    },
    tabChanged () {
      this.isChangingTab = true
      this.searchText = null
      this.blurSearch()
      if (this.isTabReservation) {
        this.reservationFilters = []
      }
      if (this.isTabPreorder) {
        this.preorderFilters = []
      }
      this.currentPage = 1
      this.fetchList()
      this.$nextTick(() => {
        this.isChangingTab = false
      })
    },
    fetchList (hideLoading) {
      if (this.isTabReservation) {
        this.fetchReservations(hideLoading)
      } else {
        this.fetchPreorders(hideLoading)
      }
    },
    fetchReservations (hideLoading) {
      if (!hideLoading) {
        this.isFetching = true
      }
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: this.ordering ?? '-id',
        statuses: 'cancelled,active',
      }
      if (this.searchText) { params.search = this.searchText }
      this.reservationFilters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params })
        .then(resp => {
          this.items = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
          this.isChangingTab = false
        })
    },
    fetchPreorders (hideLoading) {
      if (!hideLoading) {
        this.isFetching = true
      }
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: this.ordering ?? '-id',
        statuses: 'cancelled,active',
      }
      if (this.searchText) { params.search = this.searchText }
      this.preorderFilters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params })
        .then(resp => {
          this.items = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
          this.isChangingTab = false
        })
    },
    async fetchActiveCounts () {
      const params = { statuses: 'active', page_size: 1 }
      try {
        const [r, p] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params }),
        ])
        this.activeReservationCount = r.data.count || 0
        this.activePreorderCount = p.data.count || 0
      } catch (e) {
        // ignore — counts stay at last known value
      }
    },
    pageChanged () {
      this.fetchList()
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchList()
    }, 750),
    onSelectRow (data) {
      if (this.isTabReservation) {
        this.onViewReservation(data)
      } else {
        this.onViewPreorder(data)
      }
    },
    onViewReservation (item) {
      this.$router.push({
        name: 'ClinicReservationView',
        params: {
          reservationId: String(item.id),
          origin: 'ClinicReservationsPreorders',
          data: {
            params: {
              savePage: this.currentPage,
              saveTab: this.currentTabIndex,
              saveSearch: this.searchText,
              saveReservationFilters: this.reservationFilters.length ? this.$refs.reservationFilter.dataFilters() : null,
            }
          }
        }
      })
    },
    onCancelReservation (item) {
      this.selectedReservationItem = item
      this.cancelReservationType = 'reservation'
      this.showCancelReservationDialog = true
    },
    onViewPreorder (item) {
      this.$router.push({
        name: 'ClinicPreorderView',
        params: {
          preorderId: String(item.id),
          origin: 'ClinicReservationsPreorders',
          data: {
            params: {
              savePage: this.currentPage,
              saveTab: this.currentTabIndex,
              saveSearch: this.searchText,
              savePreorderFilters: this.preorderFilters.length ? this.$refs.preorderFilter.dataFilters() : null,
            }
          }
        }
      })
    },
    onCancelPreorder (item) {
      this.selectedReservationItem = item
      this.cancelReservationType = 'preorder'
      this.showCancelReservationDialog = true
    },
    async onConfirmCancelReservation () {
      const resource = this.cancelReservationType === 'reservation' ? 'reservations' : 'preorders'
      const label = this.cancelReservationType === 'reservation' ? 'Reservation' : 'Pre-order'
      this.isCancellingReservation = true
      try {
        await this.$axios.post(`/clinics/${this.company.slug}/${resource}/${this.selectedReservationItem.id}/cancel`)
        this.showMessage(['success', `${label} cancelled successfully`])
        this.showCancelReservationDialog = false
        this.fetchList()
        this.fetchActiveCounts()
      } catch (err) {
        const message = err.response?.data?.detail || this.$t('error_message.unable_to_complete_request')
        this.showMessage(['error', message])
      } finally {
        this.isCancellingReservation = false
      }
    },
    onProceedWithRefund () {
      this.showCancelReservationDialog = false
    },
    onAddNewReservation () {
      this.reservationDialogType = 'reservation'
      this.showReservationPreorderPatientDialog = true
    },
    onAddNewPreorder () {
      this.reservationDialogType = 'preorder'
      this.showReservationPreorderPatientDialog = true
    },
    onRegisterAndAddToVisit (item) {
      this.pendingRegisterItem = item
      this.showRegisterAddToVisitDialog = true
    },
    onConfirmRegisterAndAddToVisit () {
      this.showRegisterAddToVisitDialog = false
      this.tempPatient = this.pendingRegisterItem.patient.id
      this.openRegisterPatientDialog = true
    },
    closeDialog () {
      this.selectedReservationItem = null
      this.pendingRegisterItem = null
      this.tempPatient = null
      this.openRegisterPatientDialog = false
      this.showReservationPreorderPatientDialog = false
    },
    getPaymentStatusIcon (item) {
      if (item.payment_status === 'paid' || item.payment_status === 'refunded') {
        return '$yzi-check-circle-solid'
      }
      return '$yzi-alert-circle-solid'
    },
    getPaymentStatusColor (item) {
      if (item.payment_status === 'paid' || item.payment_status === 'refunded') {
        return 'success500'
      }
      return 'danger500'
    },
    getPaymentStatusLabel (item) {
      const map = {
        paid: 'Paid',
        unpaid: 'Unpaid',
        partially_refunded: 'Partially refunded',
        refunded: 'Refunded',
      }
      return map[item.payment_status] || item.payment_status || 'N/A'
    },
    getReservationStatusChipColor (status) {
      const map = {
        active: 'green',
        fulfilled: 'blue',
        cancelled: 'red',
      }
      return map[status] || 'normal'
    },
    getReservationStatusLabel (status) {
      const map = {
        active: 'Active',
        fulfilled: 'Fulfilled',
        cancelled: 'Cancelled',
      }
      return map[status] || status || '-'
    },
    getItemNames (item) {
      const products = item.products || item.lead?.products || []
      return products.map(p => p.name || p.product_name)
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.items.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-data-table__wrapper table tbody tr:hover {
    cursor: pointer;
    background: var(--v-neutral200) !important;
  }
  .table-custom {
    .v-data-table__wrapper {
      overflow: visible !important;
    }
  }
}
</style>
